import { currentUserSelector } from "Auth/state";
import { FC, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { isBandsDomain } from "shared/utils";
import { venueSelectorCreator } from "Venues/state";
import { IEditorInvite, IVenueUser } from "Venues/types";
import { RemoveEditorModal } from "../RemoveEditorModal/RemoveEditorModal";
import { RevokeEditorModal } from "../RevokeEditorModal/RevokeEditorModal";

import { EditorsOverviewModal } from "./EditorsOverviewModal";
import { InviteEditorsModal } from "./InviteEditorsModal";
import { VenueInviteEditorsModal } from "./VenueInviteEditorsModal";

export interface EditorsModalProps {
  venueId: string;
  handleClose: () => void;
  refetchUserVenues: () => void;
}

export const EditorsModal: FC<EditorsModalProps> = ({
  venueId,
  handleClose,
  refetchUserVenues,
}) => {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [removeModal, setRemoveModal] = useState<{
    showModal: boolean;
    venueUser?: IVenueUser;
  }>({ showModal: false });
  const [revokeModal, setRevokeModal] = useState<{
    showModal: boolean;
    invite?: IEditorInvite;
  }>({ showModal: false });

  const venueIdSelector = useMemo(
    () => venueSelectorCreator(venueId),
    [venueId]
  );
  const venue = useSelector(venueIdSelector);

  const currentUser = useSelector(currentUserSelector);

  const title = useMemo(() => venue?.title || "", [venue]);

  // if you remove yourself as editor, close the modal and refetch users venues
  const onRemove = (id: string) => {
    if (currentUser?.id === id) {
      handleClose();
      refetchUserVenues();
    }
  };

  if (!venue) return null;

  if (removeModal && removeModal.venueUser)
    return (
      <RemoveEditorModal
        venueUser={removeModal.venueUser}
        venue={venue}
        handleClose={() => setRemoveModal({ showModal: false })}
        onRemove={onRemove}
      />
    );

  if (showInviteModal)
    return isBandsDomain ? (
      <InviteEditorsModal
        venue={venue}
        title={title}
        handleClose={() => setShowInviteModal(false)}
      />
    ) : (
      <VenueInviteEditorsModal
        venue={venue}
        title={title}
        handleClose={() => setShowInviteModal(false)}
      />
    );

  if (revokeModal && revokeModal.invite)
    return (
      <RevokeEditorModal
        venueId={venueId}
        invite={revokeModal.invite}
        handleClose={() => setRevokeModal({ showModal: false })}
      />
    );

  return (
    <EditorsOverviewModal
      title={title}
      venue={venue}
      handleClose={handleClose}
      handleOpenEdit={() => setShowInviteModal(true)}
      showRemoveModal={(venueUser: IVenueUser) =>
        setRemoveModal({ showModal: true, venueUser })
      }
      showRevokeModal={(invite: IEditorInvite) =>
        setRevokeModal({ showModal: true, invite })
      }
    />
  );
};
